import { c as _c } from "react/compiler-runtime";
import type { PropsWithStyles } from '@morpho-org/ui-v6/types';
import type { Address } from 'viem';
import { type ChainId, ChainUtils } from '@morpho-org/blue-sdk';
import { isRequirementApproval, isRequirementSignature } from '@morpho-org/morpho-sdk';
import { ClipboardButton, WarningPopover } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { SCREENING_UNAVAILABLE_MESSAGE } from '@repo/address-screening/client';
import { Button, collapseHeight, easeOutTransition, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { Alert, ArrowDiagonalUp, Close } from '@repo/ui/icons';
import { abbreviateAddress } from '@repo/utils';
import { getBlockExplorerName, isChainId } from '@repo/web3/utils';
import { motion, useReducedMotion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWalletClient } from 'wagmi';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import SignatureDeadlineAlert from '@/components/common/SignatureDeadlineAlert/SignatureDeadlineAlert';
import TransactionProgress from '@/components/ProcessingDialog/TransactionProgress';
import { useTransactionContextV2 } from '@/contexts/Transaction';
import { Status } from '@/contexts/Transaction/StateMachine/helpers';
import { useWeb3Context } from '@/contexts/Web3Context';
import { formatTransactionRequirement, type TransactionRequirement } from '@/helpers/requirement';
import { getRequirementsErrorMessage, UnsupportedRequirementError } from '@/helpers/requirementErrors';
import { useIsDeadlineExpired } from '@/hooks/useIsDeadlineExpired';
import { trackTransactionFlowCancelled } from '@/lib/analytics/client/trackers/checkout-funnel';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { SIMULATION_LIMIT_VIOLATION_MESSAGE, SIMULATION_UNVERIFIED_MESSAGE } from '@/services/simulate/simulate';
import AcceptToContinueWithoutSimulationConfirmBox from '../common/AcceptToContinueWithoutSimulationConfirmBox/AcceptToContinueWithoutSimulationConfirmBox';
import ResultGlow from './ResultGlow';
import ResultHeroIcon from './ResultHeroIcon';
import TransactionStatusBlock from './TransactionStatusBlock';
const resolveStepDetails = ({
  step,
  txMessage,
  finalTransactionTo,
  requirements,
  chainId,
  token
}: {
  step: number | 'final';
  txMessage: string;
  finalTransactionTo: Address | undefined;
  requirements: readonly TransactionRequirement[] | undefined;
  chainId: ChainId;
  token: ConfirmDialogV2Props['token'];
}) => {
  try {
    const stepDetails = step === 'final' ? {
      content: txMessage,
      address: finalTransactionTo
    } : requirements?.[step] && token ? formatTransactionRequirement(requirements[step], chainId, token) : undefined;
    return {
      stepDetails,
      requirementFormatError: undefined
    };
  } catch (error) {
    if (!(error instanceof UnsupportedRequirementError)) throw error;
    return {
      stepDetails: undefined,
      requirementFormatError: error
    };
  }
};
export interface ConfirmDialogV2Props extends PropsWithStyles {
  txMessage?: string;
  operationContent: React.ReactNode;
  token?: {
    decimals: number;
    symbol?: string;
  } | null;
  /** Rendered above the Done button on the terminal success state only. */
  successContent?: React.ReactNode;
}
const ConfirmDialogV2 = t0 => {
  const $ = _c(164);
  const {
    txMessage: t1,
    operationContent,
    token,
    successContent
  } = t0;
  const txMessage = t1 === undefined ? "Execute your bundle" : t1;
  const [isSimulationBypassAccepted, setIsSimulationBypassAccepted] = useState(false);
  const {
    allowSimulationBypass: isSimulationBypassEnabled
  } = useFeatureFlags();
  const {
    activeTransactions,
    checkoutTracking,
    reset,
    retry,
    retryScreening,
    status,
    step,
    txHash,
    chainId,
    account,
    retryDisabled,
    isAcceptableSimulationError,
    signatureDeadline,
    isStaleNonceError,
    isLimitViolationError,
    isUnverifiedSimulationError,
    isUnconfirmedTransaction,
    safeProposalMessage,
    isAwaitingSafeExecution,
    isSafeStatusUnavailable,
    screeningError,
    isScreening
  } = useTransactionContextV2();
  const {
    account: t2,
    switchToTargetChain
  } = useWeb3Context();
  const {
    isWrongChain
  } = t2;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    data: client
  } = useWalletClient();
  const isDeadlineExpired = useIsDeadlineExpired(signatureDeadline);
  const prefersReducedMotion = useReducedMotion();
  let t3;
  if ($[0] !== prefersReducedMotion) {
    t3 = prefersReducedMotion ? {
      duration: 0
    } : easeOutTransition;
    $[0] = prefersReducedMotion;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const transition = t3;
  const isFinished = !isScreening && step === "final" && status === Status.complete;
  const phase = isScreening ? "screening" : isFinished ? "finished" : "active";
  const bodyRef = useRef(null);
  const contentRef = useRef(null);
  const doneRef = useRef(null);
  const [bodyHeight, setBodyHeight] = useState("auto");
  const [height, setHeight] = useState("auto");
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const el = bodyRef.current;
      if (!el) {
        return;
      }
      const observer = new ResizeObserver(() => {
        const measuredHeight = el.offsetHeight;
        if (measuredHeight) {
          setBodyHeight(measuredHeight);
        }
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== activeTransactions) {
    t5 = [activeTransactions];
    $[3] = activeTransactions;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const el_0 = contentRef.current;
      if (!el_0) {
        return;
      }
      const observer_0 = new ResizeObserver(() => {
        const measuredHeight_0 = el_0.offsetHeight;
        if (measuredHeight_0) {
          setHeight(measuredHeight_0);
        }
      });
      observer_0.observe(el_0);
      return () => observer_0.disconnect();
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== activeTransactions || $[7] !== phase) {
    t7 = [phase, activeTransactions];
    $[6] = activeTransactions;
    $[7] = phase;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] !== reset || $[10] !== toggleDialog) {
    t8 = () => {
      reset();
      toggleDialog(false);
    };
    $[9] = reset;
    $[10] = toggleDialog;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const onClose = t8;
  let t9;
  bb0: {
    if (isScreening) {
      t9 = "screening";
      break bb0;
    }
    if (step === "final") {
      t9 = "final";
      break bb0;
    }
    const currentRequirement = activeTransactions?.requirements?.[step];
    if (!currentRequirement) {
      t9 = "unknown";
      break bb0;
    }
    if (isRequirementSignature(currentRequirement)) {
      t9 = "signature";
      break bb0;
    }
    if (isRequirementApproval(currentRequirement)) {
      t9 = "approval";
      break bb0;
    }
    t9 = "unknown";
  }
  const cancelStep = t9;
  let t10;
  if ($[12] !== account?.isContract || $[13] !== account?.isSafe || $[14] !== cancelStep || $[15] !== chainId || $[16] !== checkoutTracking || $[17] !== onClose) {
    t10 = () => {
      if (!checkoutTracking) {
        onClose();
        return;
      }
      trackTransactionFlowCancelled({
        tracking: checkoutTracking,
        chainId,
        walletType: getWalletType(account?.isSafe ?? false, account?.isContract ?? false),
        step: cancelStep
      });
      onClose();
    };
    $[12] = account?.isContract;
    $[13] = account?.isSafe;
    $[14] = cancelStep;
    $[15] = chainId;
    $[16] = checkoutTracking;
    $[17] = onClose;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const handleCancelAction = t10;
  const canBypassSimulation = !activeTransactions?.requiresSuccessfulSimulation && isSimulationBypassEnabled && isAcceptableSimulationError && !isDeadlineExpired && !isStaleNonceError && !isUnconfirmedTransaction;
  let t11;
  let t12;
  if ($[19] !== canBypassSimulation) {
    t11 = () => {
      if (!canBypassSimulation) {
        setIsSimulationBypassAccepted(false);
      }
    };
    t12 = [canBypassSimulation];
    $[19] = canBypassSimulation;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[22] !== phase) {
    t13 = () => {
      if (phase === "finished") {
        doneRef.current?.focus({
          preventScroll: true
        });
      }
    };
    t14 = [phase];
    $[22] = phase;
    $[23] = t13;
    $[24] = t14;
  } else {
    t13 = $[23];
    t14 = $[24];
  }
  useEffect(t13, t14);
  const skipSimulation = canBypassSimulation && isSimulationBypassAccepted;
  const showSwitchChain = isWrongChain && !isDeadlineExpired && !isStaleNonceError && !isUnconfirmedTransaction;
  const isScreeningBlocked = Boolean(screeningError);
  const isScreeningUnavailable = screeningError === SCREENING_UNAVAILABLE_MESSAGE;
  const retryScreeningButtonRef = useRef(null);
  let t15;
  let t16;
  if ($[25] !== isScreeningUnavailable) {
    t15 = () => {
      if (isScreeningUnavailable) {
        retryScreeningButtonRef.current?.focus();
      }
    };
    t16 = [isScreeningUnavailable];
    $[25] = isScreeningUnavailable;
    $[26] = t15;
    $[27] = t16;
  } else {
    t15 = $[26];
    t16 = $[27];
  }
  useEffect(t15, t16);
  let t17;
  if ($[28] !== activeTransactions?.transaction) {
    t17 = activeTransactions?.transaction?.buildTx();
    $[28] = activeTransactions?.transaction;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const finalTransaction = t17;
  const finalTransactionTo = finalTransaction?.to;
  const requirements = activeTransactions?.requirements;
  let t18;
  if ($[30] !== chainId || $[31] !== finalTransactionTo || $[32] !== requirements || $[33] !== step || $[34] !== token || $[35] !== txMessage) {
    t18 = resolveStepDetails({
      step,
      txMessage,
      finalTransactionTo,
      requirements,
      chainId,
      token
    });
    $[30] = chainId;
    $[31] = finalTransactionTo;
    $[32] = requirements;
    $[33] = step;
    $[34] = token;
    $[35] = txMessage;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const {
    stepDetails,
    requirementFormatError
  } = t18;
  if (!activeTransactions) {
    let t19;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <span className="text-sm">No transaction to execute</span>;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    return t19;
  }
  let t19;
  bb1: {
    if (account?.isSafe) {
      let t20;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t20 = {
          total: 0,
          index: 0
        };
        $[38] = t20;
      } else {
        t20 = $[38];
      }
      t19 = t20;
      break bb1;
    }
    let t20;
    if ($[39] !== activeTransactions.requirements) {
      t20 = activeTransactions.requirements ?? [];
      $[39] = activeTransactions.requirements;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    const allRequirements = t20;
    if (step === "final") {
      let t21;
      if ($[41] !== allRequirements.length) {
        t21 = {
          total: allRequirements.length,
          index: allRequirements.length
        };
        $[41] = allRequirements.length;
        $[42] = t21;
      } else {
        t21 = $[42];
      }
      t19 = t21;
      break bb1;
    }
    let t21;
    if ($[43] !== allRequirements.length || $[44] !== step) {
      t21 = {
        total: allRequirements.length,
        index: step
      };
      $[43] = allRequirements.length;
      $[44] = step;
      $[45] = t21;
    } else {
      t21 = $[45];
    }
    t19 = t21;
  }
  const requirementPagination = t19;
  let t20;
  if ($[46] !== chainId || $[47] !== txHash) {
    t20 = txHash ? ChainUtils.getExplorerTransactionUrl(chainId, txHash) : "";
    $[46] = chainId;
    $[47] = txHash;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const explorerUrl = t20;
  let t21;
  if ($[49] !== chainId) {
    t21 = isChainId(chainId) ? getBlockExplorerName(chainId) : "explorer";
    $[49] = chainId;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  const explorerName = t21;
  const isSubmitted = status === Status.pending || status === Status.error && !!txHash;
  const isSignatureStep = step !== "final" && isRequirementSignature(activeTransactions?.requirements?.[step]);
  let t22;
  bb2: {
    if (isAwaitingSafeExecution) {
      t22 = step === "final" ? "Your transaction is in your Safe queue and may still execute. Check your Safe queue before starting over to avoid submitting it twice." : "Your approval is in your Safe queue and may still execute. Check your Safe queue before starting over to avoid approving twice.";
      break bb2;
    }
    if (status === Status.error && txHash) {
      t22 = account?.isSafe ? "If a transaction was already proposed in this flow, it may still execute. Check your Safe queue before starting over to avoid submitting it twice." : "If a transaction was already submitted in this flow, it may still confirm. Check it on the explorer before starting over to avoid submitting it twice.";
      break bb2;
    }
    if (status === Status.pending && step === "final") {
      t22 = account?.isSafe ? "Your transaction has been proposed to your Safe and stays in its queue until it is executed. Closing only hides its progress here." : "Your transaction has already been submitted and will keep processing onchain. Closing only hides its progress here.";
      break bb2;
    }
    if (status === Status.pending) {
      t22 = "Your approval has been submitted and will keep processing onchain, but the remaining action will not run. Wait for the approval to confirm before starting over to finish the remaining action.";
      break bb2;
    }
    if (status === Status.signing && isSignatureStep) {
      t22 = "If a signature request is still open, reject it in your wallet. A signature alone moves no funds \u2014 nothing will be submitted after you close.";
      break bb2;
    }
    if (status === Status.signing && step === "final") {
      t22 = account?.isSafe ? "If a signature request is still open, reject it in your Safe. If you already signed, the transaction may have been proposed to your Safe queue \u2014 check the queue before starting over to avoid submitting it twice." : "If a wallet request is still open, reject it in your wallet. If you already approved it, the transaction may still be submitted \u2014 check your wallet activity before starting over.";
      break bb2;
    }
    if (status === Status.signing) {
      t22 = "If a wallet request is still open, reject it in your wallet. If you already approved it, only that approval may be submitted \u2014 the requested action will not run and you will need to start over.";
      break bb2;
    }
    t22 = "Are you sure you want to close this modal? It will interrupt the current flow and you will need to start over.";
  }
  const cancelBody = t22;
  let t23;
  if ($[51] !== account?.isContract || $[52] !== explorerUrl || $[53] !== status || $[54] !== step || $[55] !== txHash) {
    t23 = step === "final" && status === Status.complete ? <>Transaction{" "}{txHash ? <ExternalLink href={explorerUrl} underlined={true}>{abbreviateAddress(txHash)}</ExternalLink> : ""}{" "}{account?.isContract ? "is sent to your smart wallet!" : "is complete!"}</> : undefined;
    $[51] = account?.isContract;
    $[52] = explorerUrl;
    $[53] = status;
    $[54] = step;
    $[55] = txHash;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  const label = t23;
  let t24;
  if ($[57] !== isFinished) {
    t24 = isFinished && <ResultGlow />;
    $[57] = isFinished;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== bodyHeight) {
    t25 = {
      height: bodyHeight
    };
    $[59] = bodyHeight;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== cancelBody || $[62] !== handleCancelAction || $[63] !== isFinished || $[64] !== isScreeningBlocked || $[65] !== isSubmitted || $[66] !== onClose || $[67] !== transition) {
    t26 = isFinished ? <div className="relative z-10"><motion.div initial={collapseHeight.initial} animate={collapseHeight.animate} transition={transition} className="overflow-hidden"><div className="pb-md flex justify-center"><ResultHeroIcon status="success" /></div></motion.div></div> : <div className="flex items-center justify-between"><span className="text-title-sm">Confirm</span><div className="gap-sm flex">{isScreeningBlocked ? <Button variant="secondary" size="icon" aria-label="Close" onClick={onClose}><Close /></Button> : <WarningPopover headerLabel={isSubmitted ? "Close dialog?" : "Cancel action?"} body={cancelBody} ctaLabel={isSubmitted ? "Close anyway" : "Exit anyway"} cancelLabel="Keep going" ctaAction={handleCancelAction}><Button variant="secondary" size="icon" aria-label="Close"><Close /></Button></WarningPopover>}</div></div>;
    $[61] = cancelBody;
    $[62] = handleCancelAction;
    $[63] = isFinished;
    $[64] = isScreeningBlocked;
    $[65] = isSubmitted;
    $[66] = onClose;
    $[67] = transition;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <ErrorServiceUnavailable minHeight="364px" />;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== isScreeningBlocked || $[71] !== screeningError) {
    t28 = isScreeningBlocked && <span role="alert" className="text-3xs text-destructive-foreground">{screeningError}</span>;
    $[70] = isScreeningBlocked;
    $[71] = screeningError;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== requirementFormatError) {
    t29 = requirementFormatError && <span role="alert" className="text-3xs text-destructive-foreground">{getRequirementsErrorMessage(requirementFormatError)}</span>;
    $[73] = requirementFormatError;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  let t30;
  if ($[75] !== client || $[76] !== isScreeningUnavailable || $[77] !== retryScreening) {
    t30 = isScreeningUnavailable && <Button ref={retryScreeningButtonRef} size="lg" onClick={() => client && retryScreening(client)} disabled={!client}>Try again</Button>;
    $[75] = client;
    $[76] = isScreeningUnavailable;
    $[77] = retryScreening;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  if ($[79] !== height) {
    t31 = {
      height
    };
    $[79] = height;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  let t32;
  if ($[81] !== chainId || $[82] !== phase) {
    t32 = phase === "screening" && <div role="status"><TransactionProgress variant="loading" chainId={chainId} content="Verifying your wallet..." /></div>;
    $[81] = chainId;
    $[82] = phase;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== account?.isSafe || $[85] !== chainId || $[86] !== explorerUrl || $[87] !== isAwaitingSafeExecution || $[88] !== isSafeStatusUnavailable || $[89] !== phase || $[90] !== requirementPagination || $[91] !== status || $[92] !== step || $[93] !== stepDetails?.address || $[94] !== stepDetails?.content || $[95] !== txHash) {
    t33 = phase === "active" && status && <TransactionStatusBlock status={status} step={step} label={stepDetails?.content} address={stepDetails?.address} chainId={chainId} stepIndex={requirementPagination.index} stepTotal={requirementPagination.total + 1} txHash={txHash} explorerUrl={explorerUrl} isSafe={(account?.isSafe ?? false) || isAwaitingSafeExecution} isAwaitingSafeExecution={isAwaitingSafeExecution} isSafeStatusUnavailable={isSafeStatusUnavailable} />;
    $[84] = account?.isSafe;
    $[85] = chainId;
    $[86] = explorerUrl;
    $[87] = isAwaitingSafeExecution;
    $[88] = isSafeStatusUnavailable;
    $[89] = phase;
    $[90] = requirementPagination;
    $[91] = status;
    $[92] = step;
    $[93] = stepDetails?.address;
    $[94] = stepDetails?.content;
    $[95] = txHash;
    $[96] = t33;
  } else {
    t33 = $[96];
  }
  let t34;
  if ($[97] !== explorerName || $[98] !== explorerUrl || $[99] !== label || $[100] !== phase || $[101] !== successContent || $[102] !== txHash) {
    t34 = phase === "finished" && <>{successContent}{txHash && <div className="gap-xs flex flex-wrap items-center">{label && <span className="text-3xs text-secondary-foreground mr-auto">{label}</span>}<div className="gap-2xs flex items-center"><ClipboardButton toCopy={txHash} toastMessage="Transaction hash copied to clipboard." tooltipMessage="Copy transaction hash." size="sm" /><ExternalLink href={explorerUrl}><Button size="sm" variant="ghost" className="text-secondary-foreground">View on {explorerName}<ArrowDiagonalUp className="size-5" /></Button></ExternalLink></div></div>}</>;
    $[97] = explorerName;
    $[98] = explorerUrl;
    $[99] = label;
    $[100] = phase;
    $[101] = successContent;
    $[102] = txHash;
    $[103] = t34;
  } else {
    t34 = $[103];
  }
  let t35;
  if ($[104] !== isDeadlineExpired || $[105] !== isScreening || $[106] !== status) {
    t35 = !isScreening && isDeadlineExpired && status !== Status.error && <SignatureDeadlineAlert />;
    $[104] = isDeadlineExpired;
    $[105] = isScreening;
    $[106] = status;
    $[107] = t35;
  } else {
    t35 = $[107];
  }
  let t36;
  if ($[108] !== account?.isSafe || $[109] !== canBypassSimulation || $[110] !== client || $[111] !== isAcceptableSimulationError || $[112] !== isDeadlineExpired || $[113] !== isLimitViolationError || $[114] !== isScreening || $[115] !== isSimulationBypassAccepted || $[116] !== isStaleNonceError || $[117] !== isUnconfirmedTransaction || $[118] !== isUnverifiedSimulationError || $[119] !== isWrongChain || $[120] !== retry || $[121] !== retryDisabled || $[122] !== safeProposalMessage || $[123] !== showSwitchChain || $[124] !== skipSimulation || $[125] !== status || $[126] !== switchToTargetChain || $[127] !== txHash) {
    t36 = !isScreening && status === Status.error && <><div className="gap-xs flex flex-col"><div className={cn("p-xs text-2xs rounded-lg", showSwitchChain ? "bg-warning-foreground/18 text-warning-foreground" : "bg-destructive-foreground/18 text-destructive-foreground")}><div role="alert" className="gap-xs flex items-center"><Alert className="shrink-0" /><span>{isStaleNonceError ? "The signature is out of date and can no longer be used. Close this dialog and start over." : isUnconfirmedTransaction ? account?.isSafe ? "Your proposal has not been confirmed yet and may still execute. Check your Safe queue \u2014 only close this dialog and start over if it was rejected or dropped, or you may submit it twice." : "Your transaction has not confirmed yet and may still go through. Check it on the explorer \u2014 only close this dialog and start over if it was dropped, or you may submit it twice." : isDeadlineExpired ? "The signature deadline has passed. Close this dialog and start over." : safeProposalMessage ? safeProposalMessage : isWrongChain ? "Your wallet is on the wrong network. Switch chain to continue." : isLimitViolationError ? SIMULATION_LIMIT_VIOLATION_MESSAGE : isUnverifiedSimulationError ? SIMULATION_UNVERIFIED_MESSAGE : isAcceptableSimulationError ? "Simulation failed \u2014 the transaction outcome couldn't be verified." : txHash ? account?.isSafe ? "The request failed. Check the last proposal in your Safe, then try again." : "The request failed. Check the last transaction on the explorer, then try again." : "The request was rejected or failed. Try again."}</span></div></div>{canBypassSimulation && <AcceptToContinueWithoutSimulationConfirmBox isAccepted={isSimulationBypassAccepted} setIsAccepted={setIsSimulationBypassAccepted} />}</div>{showSwitchChain ? <Button size="lg" onClick={switchToTargetChain}>Switch chain</Button> : <Tooltip><TooltipTrigger asChild={true}><Button onClick={() => client && retry(client, {
            skipSimulation
          })} size="lg" disabled={retryDisabled || !client || isDeadlineExpired}>{skipSimulation ? "Proceed anyway" : "Try again"}</Button></TooltipTrigger>{!client && <TooltipContent>No wallet client found</TooltipContent>}</Tooltip>}</>;
    $[108] = account?.isSafe;
    $[109] = canBypassSimulation;
    $[110] = client;
    $[111] = isAcceptableSimulationError;
    $[112] = isDeadlineExpired;
    $[113] = isLimitViolationError;
    $[114] = isScreening;
    $[115] = isSimulationBypassAccepted;
    $[116] = isStaleNonceError;
    $[117] = isUnconfirmedTransaction;
    $[118] = isUnverifiedSimulationError;
    $[119] = isWrongChain;
    $[120] = retry;
    $[121] = retryDisabled;
    $[122] = safeProposalMessage;
    $[123] = showSwitchChain;
    $[124] = skipSimulation;
    $[125] = status;
    $[126] = switchToTargetChain;
    $[127] = txHash;
    $[128] = t36;
  } else {
    t36 = $[128];
  }
  let t37;
  if ($[129] !== t32 || $[130] !== t33 || $[131] !== t34 || $[132] !== t35 || $[133] !== t36) {
    t37 = <div ref={contentRef} className="gap-md flex flex-col items-stretch">{t32}{t33}{t34}{t35}{t36}</div>;
    $[129] = t32;
    $[130] = t33;
    $[131] = t34;
    $[132] = t35;
    $[133] = t36;
    $[134] = t37;
  } else {
    t37 = $[134];
  }
  let t38;
  if ($[135] !== phase || $[136] !== t37) {
    t38 = <div key={phase}>{t37}</div>;
    $[135] = phase;
    $[136] = t37;
    $[137] = t38;
  } else {
    t38 = $[137];
  }
  let t39;
  if ($[138] !== t31 || $[139] !== t38 || $[140] !== transition) {
    t39 = <motion.div animate={t31} transition={transition} className="w-full overflow-hidden">{t38}</motion.div>;
    $[138] = t31;
    $[139] = t38;
    $[140] = transition;
    $[141] = t39;
  } else {
    t39 = $[141];
  }
  let t40;
  if ($[142] !== operationContent || $[143] !== t28 || $[144] !== t29 || $[145] !== t30 || $[146] !== t39) {
    t40 = <SectionErrorBoundary fallback={t27}>{operationContent}{t28}{t29}{t30}{t39}</SectionErrorBoundary>;
    $[142] = operationContent;
    $[143] = t28;
    $[144] = t29;
    $[145] = t30;
    $[146] = t39;
    $[147] = t40;
  } else {
    t40 = $[147];
  }
  let t41;
  if ($[148] !== isFinished || $[149] !== onClose) {
    t41 = isFinished && <Button ref={doneRef} onClick={onClose} size="lg" variant="success" fullWidth={true}>Done</Button>;
    $[148] = isFinished;
    $[149] = onClose;
    $[150] = t41;
  } else {
    t41 = $[150];
  }
  let t42;
  if ($[151] !== t40 || $[152] !== t41) {
    t42 = <div className="gap-md relative z-10 flex flex-col">{t40}{t41}</div>;
    $[151] = t40;
    $[152] = t41;
    $[153] = t42;
  } else {
    t42 = $[153];
  }
  let t43;
  if ($[154] !== t26 || $[155] !== t42) {
    t43 = <div ref={bodyRef} className="gap-md p-2xs flex flex-col">{t26}{t42}</div>;
    $[154] = t26;
    $[155] = t42;
    $[156] = t43;
  } else {
    t43 = $[156];
  }
  let t44;
  if ($[157] !== t25 || $[158] !== t43 || $[159] !== transition) {
    t44 = <motion.div animate={t25} transition={transition} className="-m-2xs shrink-0 overflow-hidden" data-testid="processing-dialog">{t43}</motion.div>;
    $[157] = t25;
    $[158] = t43;
    $[159] = transition;
    $[160] = t44;
  } else {
    t44 = $[160];
  }
  let t45;
  if ($[161] !== t24 || $[162] !== t44) {
    t45 = <>{t24}{t44}</>;
    $[161] = t24;
    $[162] = t44;
    $[163] = t45;
  } else {
    t45 = $[163];
  }
  return t45;
};
export default ConfirmDialogV2;